import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { DsButton } from "../../../design-system";
import { assetService, employeeService } from "../../../services/api";
import { ASSET_CONDITIONS, fieldLabel, inputSm } from "./constants";

export default function AssetAssignModal({
  open,
  mode = "assign", // assign | transfer | return
  asset,
  onClose,
  onSaved,
}) {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [condition, setCondition] = useState("good");
  const [accessories, setAccessories] = useState("");
  const [acknowledgement, setAcknowledgement] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [empSearch, setEmpSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setEmployeeId(asset?.assignedToEmployeeId || asset?.assigned_to_employee_id || "");
    setCondition(asset?.condition || "good");
    setAccessories("");
    setAcknowledgement(false);
    setNotes("");
    setDate(new Date().toISOString().slice(0, 10));
    let cancelled = false;
    (async () => {
      try {
        const res = await employeeService.getEmployees({ limit: 500, status: "active" });
        const list = res?.data?.employees || res?.data || res?.employees || [];
        if (!cancelled) setEmployees(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setEmployees([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, asset]);

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return employees.slice(0, 100);
    return employees
      .filter((e) => {
        const name = `${e.firstName || e.first_name || ""} ${e.lastName || e.last_name || ""}`.toLowerCase();
        const code = String(e.employeeCode || e.employee_code || e.code || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      })
      .slice(0, 100);
  }, [employees, empSearch]);

  if (!open || !asset) return null;

  const title =
    mode === "return" ? "Return Asset" : mode === "transfer" ? "Transfer Asset" : "Assign Asset";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode !== "return" && !employeeId) {
      toast.error("Select an employee");
      return;
    }
    if (!acknowledgement) {
      toast.error("Acknowledgement is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employeeId: employeeId || null,
        date,
        condition,
        accessories: accessories
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        acknowledgement: true,
        notes,
      };
      if (mode === "return") {
        await assetService.returnAsset(asset.id, payload);
        toast.success("Asset returned");
      } else if (mode === "transfer") {
        await assetService.transferAsset(asset.id, payload);
        toast.success("Asset transferred");
      } else {
        await assetService.assignAsset(asset.id, payload);
        toast.success("Asset assigned");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Action failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-lg">
        <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <h2 className="ds-h3 text-[hsl(var(--ds-fg))]">{title}</h2>
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              {asset.tag || asset.assetTag || asset.name}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form className="space-y-3 p-4" onSubmit={handleSubmit}>
          {mode !== "return" ? (
            <div>
              <label className={fieldLabel}>Employee</label>
              <input
                className={`${inputSm} mb-2`}
                placeholder="Search name or code…"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
              />
              <select
                className={inputSm}
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                required
              >
                <option value="">Select employee</option>
                {filteredEmployees.map((e) => {
                  const name = [e.firstName || e.first_name, e.lastName || e.last_name]
                    .filter(Boolean)
                    .join(" ");
                  const code = e.employeeCode || e.employee_code || e.code || "";
                  return (
                    <option key={e.id} value={e.id}>
                      {code ? `${code} — ` : ""}
                      {name || e.email || e.id}
                    </option>
                  );
                })}
              </select>
            </div>
          ) : null}
          <div>
            <label className={fieldLabel}>{mode === "return" ? "Return Date" : "Date"}</label>
            <input type="date" className={inputSm} value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div>
            <label className={fieldLabel}>Condition</label>
            <select className={inputSm} value={condition} onChange={(e) => setCondition(e.target.value)}>
              {ASSET_CONDITIONS.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={fieldLabel}>Accessories (comma-separated)</label>
            <input className={inputSm} value={accessories} onChange={(e) => setAccessories(e.target.value)} />
          </div>
          <div>
            <label className={fieldLabel}>Notes</label>
            <textarea className={inputSm} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-[hsl(var(--ds-fg))]">
            <input
              type="checkbox"
              checked={acknowledgement}
              onChange={(e) => setAcknowledgement(e.target.checked)}
            />
            Acknowledgement received
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <DsButton type="button" variant="outline" onClick={onClose}>Cancel</DsButton>
            <DsButton type="submit" disabled={saving}>{saving ? "Saving…" : "Confirm"}</DsButton>
          </div>
        </form>
      </div>
    </div>
  );
}
